type DraftKind = 'account' | 'identification';

type DraftRecord = Partial<Record<DraftKind, unknown>>;

type TurboVisitEvent = CustomEvent<{
  action: 'advance' | 'replace' | 'restore';
  url: string | URL;
}>;

type TurboSubmitEndEvent = CustomEvent<{
  success: boolean;
  formSubmission?: {
    formElement?: HTMLFormElement;
    fetchResponse?: { response?: Response };
  };
}>;

type WorkerHistoryState = Record<string, unknown> & {
  workerRegistrationDraft?: { id?: unknown };
};

const storagePrefix = 'workerRegistrationDraft:';

class RegistrationFlowDraftController {
  private initialized = false;
  private currentUrl = window.location.href;
  private pendingRestoreId: string | null = null;

  setup() {
    if (this.initialized) {
      return;
    }

    this.initialized = true;
    if (this.navigationType() === 'reload') {
      this.clearAll();
    }
    if (this.isFlowUrl(this.currentUrl)) {
      this.ensureHistoryDraftId();
    }

    document.addEventListener('turbo:visit', event => this.onTurboVisit(event as TurboVisitEvent));
    document.addEventListener('turbo:load', () => this.onTurboLoad());
    document.addEventListener('click', event => this.onClick(event));
    document.addEventListener('submit', event => this.onSubmit(event));
    document.addEventListener('turbo:submit-end', event => this.onSubmitEnd(event as TurboSubmitEndEvent));
    document.addEventListener('turbo:before-stream-render', event => this.onBeforeStreamRender(event));
    window.addEventListener('pagehide', () => this.clearAll());
  }

  save(kind: DraftKind, data: unknown) {
    if (!this.isFlowUrl(window.location.href)) {
      return;
    }

    const id = this.ensureHistoryDraftId();
    const record = this.read(id) ?? {};
    record[kind] = data;
    this.write(id, record);
  }

  restore(kind: DraftKind, pathname: string): unknown {
    if (!this.pendingRestoreId || window.location.pathname !== pathname) {
      return null;
    }

    const id = this.consumePendingRestoreId();
    return this.read(id)?.[kind] ?? null;
  }

  private clearAll() {
    this.pendingRestoreId = null;
    try {
      for (let index = sessionStorage.length - 1; index >= 0; index -= 1) {
        const key = sessionStorage.key(index);
        if (key?.startsWith(storagePrefix)) {
          sessionStorage.removeItem(key);
        }
      }
    } catch (_error) {
      return;
    }
  }

  private onTurboVisit(event: TurboVisitEvent) {
    const sourceIsFlow = this.isFlowUrl(this.currentUrl);
    const targetUrl = event.detail.url.toString();
    const targetIsFlow = this.isFlowUrl(targetUrl);

    if (!targetIsFlow) {
      this.clearAll();
      return;
    }

    this.pendingRestoreId = event.detail.action === 'restore' && sourceIsFlow
      ? this.historyDraftId()
      : null;
  }

  private onTurboLoad() {
    this.currentUrl = window.location.href;
    if (this.isFlowUrl(this.currentUrl)) {
      this.ensureHistoryDraftId();
    }
  }

  private onClick(event: Event) {
    if (!this.isFlowUrl(window.location.href)) {
      return;
    }

    const target = event.target as Element | null;
    const link = target?.closest<HTMLAnchorElement>('a[href]');
    if (!link || link.target === '_blank' || link.href.startsWith('javascript:') || link.getAttribute('href')?.startsWith('#')) {
      return;
    }
    if (!this.isFlowUrl(link.href)) {
      this.clearAll();
    }
  }

  private onSubmit(event: Event) {
    if (!this.isFlowUrl(window.location.href)) {
      return;
    }

    const form = event.target as HTMLFormElement;
    if (form.dataset.registrationDraftSuccess === 'true' || form.dataset.registrationDraftPreserve === 'true') {
      return;
    }
    const actionPath = new URL(form.action, window.location.origin).pathname.replace(/\/$/, '');
    const flowFormActions = ['/license', '/license/pre_create'];
    if (!this.isFlowUrl(form.action) && !flowFormActions.includes(actionPath)) {
      this.clearAll();
    }
  }

  private onSubmitEnd(event: TurboSubmitEndEvent) {
    const submission = event.detail.formSubmission;
    const form = submission?.formElement;
    if (!event.detail.success || form?.dataset.registrationDraftSuccess !== 'true') {
      return;
    }

    if (submission?.fetchResponse?.response?.redirected) {
      this.clearAll();
    }
  }

  private onBeforeStreamRender(event: Event) {
    const stream = event.target as Element | null;
    const template = stream?.querySelector('template');
    if (template instanceof HTMLTemplateElement && template.content.querySelector('[data-registration-draft-saved]')) {
      this.clearAll();
    }
  }

  private ensureHistoryDraftId() {
    const existing = this.historyDraftId();
    if (existing) {
      return existing;
    }

    const id = this.newId();
    const rawState = history.state as unknown;
    const state = rawState && typeof rawState === 'object' ? rawState as WorkerHistoryState : {};
    try {
      history.replaceState({ ...state, workerRegistrationDraft: { id } }, '');
    } catch (_error) {
      return id;
    }
    return id;
  }

  private historyDraftId() {
    const state = history.state as unknown as WorkerHistoryState | null;
    const id = state?.workerRegistrationDraft?.id;
    return typeof id === 'string' && id.length > 0 ? id : null;
  }

  private consumePendingRestoreId() {
    const id = this.pendingRestoreId as string;
    this.pendingRestoreId = null;
    return id;
  }

  private newId() {
    return typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }

  private read(id: string): DraftRecord | null {
    try {
      const raw = sessionStorage.getItem(`${storagePrefix}${id}`);
      if (!raw) {
        return null;
      }
      const value = JSON.parse(raw) as DraftRecord;
      return value && typeof value === 'object' ? value : null;
    } catch (_error) {
      try {
        sessionStorage.removeItem(`${storagePrefix}${id}`);
      } catch (_removeError) {
        return null;
      }
      return null;
    }
  }

  private write(id: string, record: DraftRecord) {
    try {
      sessionStorage.setItem(`${storagePrefix}${id}`, JSON.stringify(record));
    } catch (_error) {
      return;
    }
  }

  private isFlowUrl(value: string) {
    const pathname = new URL(value, window.location.origin).pathname.replace(/\/$/, '') || '/';
    return pathname === '/registration_step' ||
      pathname === '/license/new' ||
      pathname === '/account/new' ||
      pathname === '/user_identification_requests' ||
      pathname === '/user_identification_requests/new';
  }

  private navigationType() {
    if (typeof performance.getEntriesByType !== 'function') {
      return undefined;
    }
    return (performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined)?.type;
  }
}

export default new RegistrationFlowDraftController();
